import React, { useEffect, useRef, useState, useMemo } from 'react';
import Button from '../../../../../../../common/element/Buttons';
import ToolManagmentCard from '../../../../../../../common/element/Card/ToolManagmnetCard';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserData } from '../../../../../../../redux/auth/authSelector';
import { setBreadcrumbs } from '../../../../../../../redux/Admin/breadcrumbsSlice';
import { getSessionAssessmentLink, getSessionAssignmentResult, getSessionDocumentLink, submitAssignmentAnswers } from '../../../../../../../redux/Employee/employeesSlice';
import {
  selectSessionAssessmentLink,
  selectSessionAssessmentLinkLoading,
  selectSessionAssignmentResult,
  selectSessionAssignmentResultLoading,
  selectSessionDocumentLink,
  selectSubmitAssignmentAnswersLoading,
  selectSessionDocumentLinkLoading
} from '../../../../../../../redux/Employee/employeesSelector';
import { useLocation } from 'react-router-dom';
import ThreeDotBouncingLoader from '../../../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder';
import EmployeeAssignmentDialog from '../../../../Assignments/EmployeeAssignmentView';
import { formatToolName } from '../../../../../../../common/Formatters.js/formatters';
import { ROLES } from '../../../../../../../config/rolePermissions';
const OverViewEmployee = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const EmployeeId = searchParams.get('employeeId');
  const SessionId = searchParams.get('sessionId');
  const sessionName = searchParams.get('sessionName');
  const storedSessionName = useSelector((state) => state.sessionView?.sessionName);
  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;
  const isAdmin = userData?.role_name === ROLES.ADMIN;
  const isCoach = userData?.role_name === ROLES.COACH;
  const submitLoading = useSelector(selectSubmitAssignmentAnswersLoading);

  const dispatch = useDispatch();

  // Get data from Redux
  const documentLinkData = useSelector(selectSessionDocumentLink);
  const documentLinkLoading = useSelector(selectSessionDocumentLinkLoading);
  const assessmentLinkData = useSelector(selectSessionAssessmentLink);
  const assessmentLinkLoading = useSelector(selectSessionAssessmentLinkLoading);
  const selectSessionAssignmentLoading = useSelector(selectSessionAssignmentResultLoading);
  const assignmentResultData = useSelector(selectSessionAssignmentResult);

  // --- Logic for Assignments ---
  const isAssignmentNull = assignmentResultData?.data === null;
  const assignmentsList = assignmentResultData?.data?.assignments || assignmentResultData?.assignments || [];
  const hasAssignments = assignmentsList.length > 0;

  // Dialog State
  const [assignmentDialogBox, setAssignmentDialogBox] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [dialogMode, setDialogMode] = useState('edit');

  // --- Logic for Opening Dialog ---
  const handleOpenAssignment = (assignment, mode) => {
    setSelectedAssignment(assignment);
    setDialogMode(mode);
    setAssignmentDialogBox(true);
  };

  // --- Logic for Submitting Answers ---
  const handleRefresh = async (payload) => {
    setAssignmentDialogBox(false);
    setSelectedAssignment(null);
    dispatch(getSessionAssignmentResult({ sessionId: SessionId, employeeId: EmployeeId }));
  };

  useEffect(() => {
    if (userRole === "HR" || userRole === "Employee" || userRole === "Company Admin" || userRole === "Management" || userRole === "Science Of Self" || userRole === "Sales Training Company") {
      const label = storedSessionName || sessionName || 'Session View';
      const crumbs = [
        { label: "Session", url: "/sessions", home: "/sessions" },
        { label: label, url: `/sessions/view?sessionId=${SessionId}&sessionName=${label}`, home: "/sessions/view" },
      ];
      dispatch(setBreadcrumbs(crumbs));
    }
  }, [dispatch, userRole, SessionId, sessionName, storedSessionName]);

  useEffect(() => {
    const sessionId = SessionId;
    const employeeId = EmployeeId;

    dispatch(getSessionDocumentLink({ sessionId, employeeId }));
    dispatch(getSessionAssessmentLink({ sessionId, employeeId }));
    dispatch(getSessionAssignmentResult({ sessionId, employeeId }));

  }, [dispatch, SessionId, EmployeeId]);


  // --- Helper variables for Assessment Logic ---
  const assessmentInfo = assessmentLinkData;
  const resultData = assessmentInfo; // Keeping consistent with your code
  const toolId = resultData?.tool_id;

  const isNCodesResult = toolId === 1 || toolId === 2 || toolId === 3;
  const isInnerDriveResult = toolId === 4;
  const isHumanNeedsResult = toolId === 8;
  const isBaselineResult = toolId === 5;
  const isPersonalityTestResult = toolId === 6;
  const isTraitsTestResult = toolId === 7;

  // --- Logic Extracted from ViewResultDialog ---

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // For Tool 7 (Traits)
  const traitsTestRows = useMemo(() => {
    if (isTraitsTestResult && resultData?.score) {
      const scoreData = resultData.score;
      const traits = [];
      const traitNames = Object.keys(scoreData)
        .filter(key => key.endsWith('Level'))
        .map(key => key.replace('Level', ''));

      traitNames.forEach(trait => {
        const levelKey = `${trait}Level`;
        const scoreKey = `${trait}Score`;
        traits.push({
          name: trait.charAt(0).toUpperCase() + trait.slice(1),
          level: scoreData[levelKey] || '',
          score: scoreData[scoreKey] || 0
        });
      });
      return traits.sort((a, b) => b.score - a.score);
    }
    return [];
  }, [isTraitsTestResult, resultData]);

  // For Tool 6 (Personality)
  const personalityTestRows = useMemo(() => {
    if (isPersonalityTestResult && resultData?.score) {
      return Object.entries(resultData.score)
        .filter(([key]) => key !== 'user_ordered_items')
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isPersonalityTestResult, resultData]);

  const personalityAssessmentType = useMemo(() => {
    if (isPersonalityTestResult && resultData?.score?.user_ordered_items?.assessment_type) {
      return resultData.score.user_ordered_items.assessment_type.map((name) => ({
        name,
        score: resultData.score[name] ?? 0,
      }));
    }
    return [];
  }, [isPersonalityTestResult, resultData]);

  const personalityCoachType = useMemo(() => {
    if (isPersonalityTestResult && resultData?.score?.user_ordered_items?.coach_type) {
      return resultData.score.user_ordered_items.coach_type.map((name) => ({
        name,
        score: resultData.score[name] ?? 0,
      }));
    }
    return [];
  }, [isPersonalityTestResult, resultData]);

  // For Tool 4 (Inner Drive) - top 2 as per assessment
  const innerDriveAssessmentRows = useMemo(() => {
    if (isInnerDriveResult && resultData?.score?.user_ordered_items?.top_assessment) {
      return resultData.score.user_ordered_items.top_assessment.map((name) => ({
        name,
        score: resultData.score[name] ?? 0,
      }));
    }
    return [];
  }, [isInnerDriveResult, resultData]);

  // For Tool 4 (Inner Drive) - top 2 after coach discussion
  const innerDriveCoachRows = useMemo(() => {
    if (isInnerDriveResult && resultData?.score?.user_ordered_items?.top_after_coach) {
      return resultData.score.user_ordered_items.top_after_coach.map((name) => ({
        name,
        score: resultData.score[name] ?? 0,
      }));
    }
    return [];
  }, [isInnerDriveResult, resultData]);

  // For Tool 4 (Inner Drive) - fallback when user_ordered_items is absent
  const innerDriveFallbackRows = useMemo(() => {
    if (isInnerDriveResult && resultData?.score) {
      return [
        { key: 'Certainty', value: resultData.score.Certainty || 0 },
        { key: 'Variety', value: resultData.score.Variety || 0 },
        { key: 'Significance', value: resultData.score.Significance || 0 },
        { key: 'Love/Connection', value: resultData.score['Love/Connection'] || 0 },
        { key: 'Growth', value: resultData.score.Growth || 0 },
        { key: 'Contribution', value: resultData.score.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isInnerDriveResult, resultData]);

  // For Tool 8 (Human Needs)
  const humanNeedsRows = useMemo(() => {
    if (isHumanNeedsResult && resultData?.score) {
      return [
        { key: 'Certainty', value: resultData?.score?.Certainty || 0 },
        { key: 'Variety', value: resultData?.score?.Variety || 0 },
        { key: 'Significance', value: resultData?.score?.Significance || 0 },
        { key: 'Love/Connection', value: resultData?.score?.['Love/Connection'] || 0 },
        { key: 'Growth', value: resultData?.score?.Growth || 0 },
        { key: 'Contribution', value: resultData?.score?.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isHumanNeedsResult, resultData]);

  // Highlighting Logic
  const highestPersonalityScore = personalityTestRows.length > 0 ? personalityTestRows[0]?.value : 0;


  const handleStartAssessment = () => {
    if (assessmentInfo?.assessmentLink) {
      window.open(assessmentInfo.assessmentLink, '_blank');
    }
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileType) => {
    const iconMap = {
      'pdf': (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="32" height="32" rx="16" fill="#FF8E1C" />
          <rect x="2" y="2" width="32" height="32" rx="16" stroke="#FFD7A1" strokeWidth="4" />
          <path d="M21.6424 20.2861C21.4593 20.34 21.1905 20.3461 20.9023 20.3044C20.593 20.2596 20.2775 20.1652 19.9674 20.0261C20.5204 19.9458 20.9494 19.9706 21.3163 20.1003C21.4032 20.1311 21.546 20.2133 21.6424 20.2861ZM18.557 19.7797C18.5345 19.7858 18.5124 19.7916 18.4903 19.7977C18.3415 19.8381 18.1967 19.8776 18.0572 19.9127L17.8691 19.9603C17.4907 20.0559 17.1038 20.1536 16.7216 20.2698C16.8668 19.9202 17.0018 19.5667 17.1339 19.2211C17.2317 18.9653 17.3316 18.704 17.4349 18.446C17.4873 18.5324 17.542 18.6189 17.5989 18.7055C17.8582 19.0997 18.1841 19.4642 18.557 19.7797ZM17.5944 15.8372C17.619 16.2686 17.5257 16.6836 17.389 17.0818C17.2205 16.5895 17.142 16.0458 17.3526 15.6069C17.4066 15.4944 17.4509 15.4343 17.4796 15.4029C17.5239 15.4712 17.5822 15.6241 17.5944 15.8372ZM15.6188 21.302C15.5241 21.4711 15.4275 21.6293 15.3285 21.7788C15.0895 22.1383 14.6986 22.5233 14.4978 22.5233C14.4781 22.5233 14.4542 22.5201 14.4192 22.4833C14.3967 22.4597 14.3931 22.4429 14.3942 22.4198C14.401 22.2876 14.5765 22.0522 14.8306 21.8339C15.0613 21.6359 15.3221 21.4598 15.6188 21.302ZM22.2817 20.3048C22.2511 19.8645 21.5085 19.582 21.5012 19.5793C21.2142 19.4778 20.9024 19.4284 20.548 19.4284C20.1686 19.4284 19.7596 19.4832 19.2343 19.6057C18.7669 19.275 18.3632 18.8609 18.0615 18.4025C17.9283 18.1999 17.8086 17.9978 17.7042 17.8003C17.959 17.192 18.1885 16.538 18.1468 15.8055C18.1132 15.2181 17.8479 14.8236 17.4871 14.8236C17.2396 14.8236 17.0264 15.0066 16.8532 15.368C16.544 16.012 16.6253 16.8361 17.0946 17.8194C16.9255 18.2157 16.7685 18.6265 16.6165 19.0242C16.4274 19.5187 16.2326 20.0289 16.013 20.5142C15.3971 20.7575 14.8912 21.0525 14.4695 21.4146C14.1933 21.6515 13.8603 22.0135 13.8413 22.3915C13.832 22.5695 13.8932 22.7328 14.0174 22.8636C14.1494 23.0024 14.3152 23.0756 14.4976 23.0758C15.0998 23.0758 15.6794 22.2498 15.7894 22.0841C16.0107 21.7511 16.2178 21.3797 16.4208 20.9512C16.932 20.7668 17.4767 20.6291 18.0048 20.4961L18.1939 20.4481C18.336 20.412 18.4837 20.3721 18.6353 20.3307C18.7956 20.2875 18.9606 20.2426 19.1283 20.1999C19.6705 20.5442 20.2535 20.7687 20.8222 20.8511C21.3011 20.9206 21.7265 20.8803 22.0144 20.7304C22.2735 20.5957 22.2878 20.3878 22.2817 20.3048ZM23.4481 24.0911C23.4481 24.8975 22.7361 24.9472 22.5925 24.9488H13.4068C12.6019 24.9488 12.5534 24.2332 12.5519 24.0911L12.5518 11.9087C12.5518 11.1015 13.265 11.0525 13.4066 11.0509H19.6118L19.6151 11.0542V13.4715C19.6151 13.9566 19.9089 14.8751 21.0215 14.8751H23.4274L23.448 14.8957L23.4481 24.0911ZM22.8777 14.3241H21.0219C20.2173 14.3241 20.1686 13.6124 20.1675 13.4716V11.6074L22.8777 14.3241ZM24 24.0911V14.6684L20.1675 10.8266V10.8088H20.1492L19.8414 10.5H13.4068C12.9202 10.5 12 10.7945 12 11.9089V24.0913C12 24.5785 12.2941 25.5 13.4068 25.5H22.5933C23.0798 25.4999 24 25.2054 24 24.0911Z" fill="white" />
        </svg>
      ),
      'docx': (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="32" height="32" rx="16" fill="#FF8E1C" />
          <rect x="2" y="2" width="32" height="32" rx="16" stroke="#FFD7A1" strokeWidth="4" />
          <path fillRule="evenodd" clipRule="evenodd" d="M16.1578 10C15.9624 10 15.775 10.0766 15.6368 10.213L11.2158 14.5766C11.0776 14.713 11 14.898 11 15.0909V23.8182C11 25.0232 11.9897 26 13.2105 26H16.1578C16.5648 26 16.8947 25.6744 16.8947 25.2727C16.8947 24.8711 16.5648 24.5455 16.1578 24.5455H13.2105C12.8036 24.5455 12.4737 24.2199 12.4737 23.8182V15.8182H16.1578C16.5648 15.8182 16.8947 15.4926 16.8947 15.0909V11.4545H22.0525C22.4594 11.4545 22.7893 11.7802 22.7893 12.1818V15.8182C22.7893 16.2198 23.1192 16.5455 23.5262 16.5455C23.9331 16.5455 24.263 16.2198 24.263 15.8182V12.1818C24.263 10.9768 23.2733 10 22.0525 10H16.1578ZM15.421 14.3636H13.5157L15.421 12.4831V14.3636ZM18.333 18.5047C18.2084 18.1223 17.7935 17.912 17.406 18.0349C17.0186 18.1578 16.8055 18.5674 16.93 18.9498L18.5879 24.0407C18.6858 24.3415 18.9693 24.5455 19.2894 24.5455C19.6094 24.5455 19.8929 24.3415 19.9908 24.0407L20.9472 21.1039L21.9037 24.0407C22.0016 24.3415 22.285 24.5455 22.6051 24.5455C22.9252 24.5455 23.2087 24.3415 23.3066 24.0407L24.9645 18.9498C25.089 18.5674 24.8759 18.1578 24.4885 18.0349C24.101 17.912 23.686 18.1223 23.5615 18.5047L22.6051 21.4415L21.6487 18.5047C21.5508 18.2039 21.2673 18 20.9472 18C20.6272 18 20.3437 18.2039 20.2458 18.5047L19.2894 21.4415L18.333 18.5047Z" fill="white" />
        </svg>
      ),
      'jpg': (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="32" height="32" rx="16" fill="#FF8E1C" />
          <rect x="2" y="2" width="32" height="32" rx="16" stroke="#FFD7A1" strokeWidth="4" />
          <path d="M13 14.5C13 13.6716 13.6716 13 14.5 13C15.3284 13 16 13.6716 16 14.5C16 15.3284 15.3284 16 14.5 16C13.6716 16 13 15.3284 13 14.5Z" fill="white" />
          <path fillRule="evenodd" clipRule="evenodd" d="M12 10C10.8954 10 10 10.8954 10 12V24C10 25.1046 10.8954 26 12 26H24C25.1046 26 26 25.1046 26 24V12C26 10.8954 25.1046 10 24 10H12ZM24 12H12V19.5858L15.2929 16.2929C15.6834 15.9024 16.3166 15.9024 16.7071 16.2929L20 19.5858L21.2929 18.2929C21.6834 17.9024 22.3166 17.9024 22.7071 18.2929L24 19.5858V12ZM24 21.4142L22 19.4142L20.7071 20.7071C20.3166 21.0976 19.6834 21.0976 19.2929 20.7071L16 17.4142L12 21.4142V24H24V21.4142Z" fill="white" />
        </svg>
      ),
      'png': (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="32" height="32" rx="16" fill="#FF8E1C" />
          <rect x="2" y="2" width="32" height="32" rx="16" stroke="#FFD7A1" strokeWidth="4" />
          <path d="M13 14.5C13 13.6716 13.6716 13 14.5 13C15.3284 13 16 13.6716 16 14.5C16 15.3284 15.3284 16 14.5 16C13.6716 16 13 15.3284 13 14.5Z" fill="white" />
          <path fillRule="evenodd" clipRule="evenodd" d="M12 10C10.8954 10 10 10.8954 10 12V24C10 25.1046 10.8954 26 12 26H24C25.1046 26 26 25.1046 26 24V12C26 10.8954 25.1046 10 24 10H12ZM24 12H12V19.5858L15.2929 16.2929C15.6834 15.9024 16.3166 15.9024 16.7071 16.2929L20 19.5858L21.2929 18.2929C21.6834 17.9024 22.3166 17.9024 22.7071 18.2929L24 19.5858V12ZM24 21.4142L22 19.4142L20.7071 20.7071C20.3166 21.0976 19.6834 21.0976 19.2929 20.7071L16 17.4142L12 21.4142V24H24V21.4142Z" fill="white" />
        </svg>
      ),
    };
    return iconMap[fileType?.toLowerCase()] || iconMap['pdf'];
  };

  // Helper function to extract filename from URL
  const getFilenameFromUrl = (url) => {
    if (!url) return 'Unknown file';
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    const cleanFilename = filename.replace(/^\d+-\d+-/, '');
    return cleanFilename;
  };

  // Helper function to handle document download
  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {(userRole === "HR" || userRole === "Employee" || userRole === "Company Admin" || userRole === "Management" || userRole === "Science Of Self" || userRole === "Sales Training Company") && (
        <div>
          <h1 className="font-lato font-semibold text-[28px] leading-[150%] tracking-[0%] text-start mb-4">
            {sessionName}
          </h1>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Assessment and Resources */}
        <div className="lg:col-span-1 space-y-6">
          {/* Assessment Result Card */}
          <ToolManagmentCard>
            <div className="pb-3 flex items-center justify-between">
              <h2 className="font-lato not-italic font-semibold text-[16px] leading-[150%] text-[#101828]">
                Assessment
              </h2>
            </div>
            <div className='border-b border-gray-200 mb-3'></div>

            {assessmentLinkLoading ? (
              <div className="flex items-center justify-center py-8">
                <ThreeDotBouncingLoader />
              </div>
            ) : (!assessmentInfo || (assessmentInfo.success === true && assessmentInfo.data === null)) ? (
              /* Scenario 1: No data available */
              <div className="flex items-center justify-center py-8">
                <p className="font-lato font-normal text-[14px] text-[#475467]">No Assessment available</p>
              </div>
            ) : assessmentInfo.status === 'pending' ? (
              /* Scenario 2: Pending Status - Show Start Button */
              isAdmin || isCoach ? (
                <div className="flex flex-col items-center justify-center py-8 px-4">
                  <p className="font-lato font-normal text-[14px] leading-[150%] text-center text-[#475467] mb-4">
                    Assessment Name : {formatToolName(assessmentInfo?.tool_name)}
                  </p>
                  <p className="font-lato font-normal text-[14px] leading-[150%] text-center text-[#475467] mb-2">
                    Not Completed Yet.
                  </p>

                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 px-4">
                  <p className="font-lato font-normal text-[14px] leading-[150%] text-center text-[#475467] mb-4">
                    Click the Start Assessment button to start the assessment. Results will be shown after completion.
                  </p>
                  <Button
                    onClick={handleStartAssessment}
                    className="px-[18px] py-[10px] rounded-[8px] bg-[#FF8E1C] hover:bg-[#E67E0F]
        text-white text-[14px] font-semibold leading-tight shadow-sm"
                  >
                    Start Assessment
                  </Button>
                </div>
              )


            ) : (
              /* Scenario 3: Completed - Show Result Table based on Tool ID */
              <div className="bg-white overflow-hidden">

                {/* --- ADDED: Display Tool Name Here --- */}
                {resultData?.tool_name && !(isPersonalityTestResult && personalityAssessmentType.length > 0) && (
                  <h3 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] mb-3 text-start">
                    {formatToolName(resultData.tool_name)}
                  </h3>
                )}

                {/* TOOL ID 1, 2, 3: N-Codes / Inner Drives Assessment */}
                {isNCodesResult && (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FFF4E5]">
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          N-Code
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          E-Code
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-start">
                      {resultData?.score?.selected_codes?.map((item, index) => (
                        <tr key={item.id || index} className="border-t border-gray-200">
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.ncode}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.ecode}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* TOOL ID 6: Personality Test */}
                {isPersonalityTestResult && (
                  <div className="space-y-6">
                    {personalityAssessmentType.length === 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="bg-[#FFF4E5]">
                            <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467] text-start">
                              Personality Type
                            </th>
                            <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467] text-start">
                              Score
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white text-start">
                          {personalityTestRows.map((item, index) => (
                            <tr key={index} className="border-t border-gray-200">
                              <td className={`py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-start ${item.value === highestPersonalityScore ? 'text-orange-600 font-semibold' : 'text-[#1D2939]'}`}>
                                {item.key}
                              </td>
                              <td className={`py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-start ${item.value === highestPersonalityScore ? 'text-orange-600' : 'text-[#1D2939]'}`}>
                                {item.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {personalityAssessmentType.length > 0 && (
                      <div>
                        <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                          Personality Type – As Per Assessment:
                        </h4>
                        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-[#FFF4E5]">
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Rank</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Personality Type</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Score</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {personalityAssessmentType.map((item, index) => (
                              <tr key={index} className="border-t border-gray-200 text-start">
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{index + 1}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.name}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {personalityCoachType.length > 0 && (
                      <div>
                        <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                          Personality Type – After Discussion With Coach:
                        </h4>
                        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-[#FFF4E5]">
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Rank</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Personality Type</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Score</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {personalityCoachType.map((item, index) => (
                              <tr key={index} className="border-t border-gray-200 text-start">
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{index + 1}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.name}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* TOOL ID 5: Baseline */}
                {isBaselineResult && (
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-center">
                        System Generated Baseline
                      </h4>
                      <div className="flex flex-col gap-3">
                        {resultData?.score?.selected_questions?.map((item, index) => (
                          <div
                            key={item.id}
                            className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm"
                          >
                            {index + 1}. {item.text}
                          </div>
                        ))}
                      </div>
                    </div>
                    {resultData?.score?.user_ordered_items?.length > 0 && (
                      <div>
                        <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-center">
                          User Reordered Baseline
                        </h4>
                        <div className="flex flex-col gap-3">
                          {resultData.score.user_ordered_items.map((item, index) => (
                            <div
                              key={item.id}
                              className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm"
                            >
                              {index + 1}. {item.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TOOL ID 7: Traits Test Results */}
                {isTraitsTestResult && (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FFF4E5]">
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          S No.
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          Trait
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          Level
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-start">
                      {traitsTestRows.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {String(index + 1).padStart(2, "0")}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.name}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {capitalizeFirstLetter(item.level)}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.score}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* TOOL ID 4: Inner Drive Assessment */}
                {isInnerDriveResult && (
                  innerDriveAssessmentRows.length > 0 ? (
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                        Top 2 Inner Drives – As Per Assessment:
                        </h4>
                        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-[#FFF4E5]">
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Rank</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Inner Drive</th>
                              <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Score</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {innerDriveAssessmentRows.map((item, index) => (
                              <tr key={item.name} className="border-t border-gray-200 text-start">
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{index + 1}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.name}</td>
                                <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {innerDriveCoachRows.length > 0 && (
                        <div>
                          <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                            Final Top 2 Inner Drives – After Discussion With Coach:
                          </h4>
                          <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                            <thead>
                              <tr className="bg-[#FFF4E5]">
                                <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Rank</th>
                                <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Inner Drive</th>
                                <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Score</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {innerDriveCoachRows.map((item, index) => (
                                <tr key={item.name} className="border-t border-gray-200 text-start">
                                  <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{index + 1}</td>
                                  <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.name}</td>
                                  <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#FFF4E5]">
                          <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">S No.</th>
                          <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Personality Type</th>
                          <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">Score</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white text-start">
                        {innerDriveFallbackRows.map((item, index) => (
                          <tr key={item.key} className="border-t border-gray-200 hover:bg-gray-50">
                            <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{String(index + 1).padStart(2, '0')}</td>
                            <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.key}</td>
                            <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">{item.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}

                {/* TOOL ID 8: Human Needs */}
                {isHumanNeedsResult && (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FFF4E5]">
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          S No.
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          Personality Type
                        </th>
                        <th className="py-3 px-4 text-left font-lato font-medium text-sm leading-[1.5] text-[#475467]">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-start">
                      {humanNeedsRows.map((item, index) => (
                        <tr key={item.key} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {String(index + 1).padStart(2, "0")}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.key}
                          </td>
                          <td className="py-3 px-4 font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                            {item.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {!isNCodesResult && !isPersonalityTestResult && !isBaselineResult && !isTraitsTestResult && !isHumanNeedsResult && !isInnerDriveResult && (
                  <div className="flex items-center justify-center py-8">
                    <p className="font-lato font-normal text-[14px] text-[#475467]">
                      Results are available.
                    </p>
                  </div>
                )}
              </div>
            )}
          </ToolManagmentCard>

          {/* Assigned Resources Card */}
          <ToolManagmentCard>
            <div className="pb-3">
              <h2 className="font-lato not-italic font-semibold text-[16px] leading-[150%] text-[#101828] text-start">
                Supported Documents
              </h2>
            </div>
            <div className='border-b border-gray-200 mb-3'></div>

            {documentLinkLoading ? (
              <div className="flex items-center justify-center py-8">
                <p className="font-lato font-normal text-[14px] text-[#475467]"><ThreeDotBouncingLoader /></p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentLinkData?.map((supportedDoc, index) => (
                  <div key={supportedDoc.id || index} className="space-y-3 text-start">
                    <h3 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828]">
                      {supportedDoc.supportedDocument?.name}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {supportedDoc.supportedDocument?.documents?.map((doc, docIndex) => (
                        <div
                          key={doc.document_id || docIndex}
                          className={`flex items-center gap-3 p-3 border border-gray-200 rounded-lg transition-colors
      ${isAdmin ? "bg-white cursor-pointer hover:bg-gray-50" : " cursor-not-allowed "}
    `}
                          onClick={() => {
                            if (isAdmin) {
                              handleDownload(doc.document_url, getFilenameFromUrl(doc.document_url));
                            }
                          }}
                        >
                          <div className="flex-shrink-0">
                            {getFileIcon(doc.file_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-lato font-medium text-[14px] leading-[150%] text-[#475467] truncate">
                              {getFilenameFromUrl(doc.document_url)}
                            </p>
                            <p className="font-lato font-normal text-[12px] leading-[150%] text-[#98A2B3]">
                              {doc.document_size}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {(!documentLinkData || documentLinkData.length === 0) && (
                  <div className="flex items-center justify-center py-8">
                    <p className="font-lato font-normal text-[14px] text-[#475467]">No documents available</p>
                  </div>
                )}
              </div>
            )}
          </ToolManagmentCard>
        </div>

        {/* Right Column - Homework Activity / Assignments */}
        <div className="lg:col-span-1 space-y-6">
          <ToolManagmentCard>
            <div className="pb-3 flex items-center justify-between">
              <h2 className="font-lato not-italic font-semibold text-[16px] leading-[150%] text-[#101828] text-start">
                Assignments
              </h2>
            </div>
            <div className='border-b border-gray-200 mb-3'></div>

            {selectSessionAssignmentLoading ? (
              <div className="flex items-center justify-center py-8">
                <ThreeDotBouncingLoader />
              </div>
            ) : isAssignmentNull || !hasAssignments ? (
              <div className="flex items-center justify-center py-8">
                <p className="font-lato font-normal text-[14px] text-[#475467]">
                  No assignment yet assign
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignmentsList.map((assignment, assignIndex) => (
                  <div key={assignment.session_assignment_id || assignIndex}>
                    {/* Check if questions exist */}
                    {assignment.questions && assignment.questions.length > 0 ? (

                      // CARD STYLE DISPLAY
                      <div className="p-4 border border-[#EAECF0] rounded-[12px] bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                        {/* Assignment Title */}
                        <h3 className="font-lato font-medium text-[16px] leading-[150%] text-[#101828] mb-3 text-start">
                          {assignment.assignment_name || assignment.title || `Assignment ${assignIndex + 1}`}
                        </h3>

                        {/* Button Action */}
                        <div className="flex justify-start gap-2">
                          {assignment.session_assignment_status === 'pending' ? (
                            isAdmin || isCoach ? "Not Completed Yet." : (
                              <Button
                                onClick={() => handleOpenAssignment(assignment, 'edit')}
                                className="px-[16px] py-[8px] rounded-[8px] bg-[#FF8E1C] hover:bg-[#E67E0F]
                                           text-white text-[14px] font-semibold leading-[20px] shadow-sm"
                              >
                                Start Assignment
                              </Button>
                            )
                          ) : assignment.session_assignment_status === 'draft' ? (
                            isAdmin || isCoach ? "Not Completed Yet." : (
                              <Button
                                onClick={() => handleOpenAssignment(assignment, 'edit')}
                                className="px-[16px] py-[8px] rounded-[8px] bg-[#FF8E1C] hover:bg-[#E67E0F]
                                           text-white text-[14px] font-semibold leading-[20px] shadow-sm"
                              >
                                View/Edit Assignment
                              </Button>
                            )
                          ) : (
                            <Button
                              onClick={() => handleOpenAssignment(assignment, 'view')}
                              className="px-[16px] py-[8px] rounded-[8px] bg-[#FF8E1C] hover:bg-[#E67E0F]
                                         text-white text-[14px] font-semibold leading-[20px] shadow-sm"
                            >
                              View Assignment
                            </Button>
                          )}
                        </div>
                      </div>

                    ) : (
                      // Fallback for no questions (optional, but good for debugging)
                      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-500">No questions found for this assignment.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ToolManagmentCard>
        </div>
      </div>
      {/* RENDER THE DIALOG */}
      <EmployeeAssignmentDialog
        dialogOpen={assignmentDialogBox}
        onDialogClose={() => setAssignmentDialogBox(false)}
        assignment={selectedAssignment}
        // onSubmit={handleSubmitAssignment}
        handleRefresh={handleRefresh}
        loading={submitLoading}
        mode={dialogMode} // Pass the mode here
      />
    </>
  );
};

export default OverViewEmployee;